import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import ErrorOutlineRounded from "@mui/icons-material/ErrorOutlineRounded";
import ScheduleRounded from "@mui/icons-material/ScheduleRounded";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Paper,
    Skeleton,
    Stack,
    Typography,
} from "@mui/material";
import type { GridRenderCellParams, GridSortModel } from "@mui/x-data-grid-pro";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxChip from "components/UI/AcxChip";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxDataGridStore from "components/UI/AcxDataGrid/AcxDataGridStore";
import { dateTimeColumnType } from "components/UI/AcxDataGrid/ColumnTypes/DateColTypes";
import IColDef from "components/UI/AcxDataGrid/IColDef";
import AcxDateRangeInput from "components/UI/Calendar/DateRange/AcxDateRangeInput";
import AcxMainTextField from "components/UI/AcxMainTextField";
import { observer } from "mobx-react";
import moment, { Moment } from "moment";
import React from "react";
import { useNavigate } from "react-router-dom";
import { EmailSendsService } from "services/EmailSendsService";
import { AuthStore } from "stores/AuthStore";
import useDebounce from "utils/useDebounce";
import { sanitizeHtml } from "utils/sanitizeHtml";
import { useStore } from "utils/useStore";
import {
    SendLogEntry,
    SendLogEmailPreview,
    getSendStatusPresentation,
} from "../Models/SendLog";

const PAGE_SIZE_OPTIONS = [10, 25, 50];
const RECIPIENT_SEARCH_DEBOUNCE_MS = 300;

type SendLogPageProps = {
    /** Set only by the Admin organization route, where analysis review is available. */
    adminOrganizationId?: string;
};

function statusIcon(status?: string | null) {
    switch (status?.trim().toLowerCase()) {
        case "sent":
            return <CheckCircleOutlineRounded fontSize="small" />;
        case "failed":
            return <ErrorOutlineRounded fontSize="small" />;
        default:
            return <ScheduleRounded fontSize="small" />;
    }
}

export const SendLogPage = observer(({ adminOrganizationId }: SendLogPageProps) => {
    const messageStore = useStore(MessageStore);
    const authStore = useStore(AuthStore);
    const service = React.useMemo(() => new EmailSendsService(), []);
    const navigate = useNavigate();

    const [recipient, setRecipient] = React.useState("");
    const debouncedRecipient = useDebounce(
        recipient,
        RECIPIENT_SEARCH_DEBOUNCE_MS,
    );
    const [startDate, setStartDate] = React.useState<Moment>(() =>
        moment().subtract(30, "days"),
    );
    const [endDate, setEndDate] = React.useState<Moment>(() => moment());

    const [page, setPage] = React.useState(0);
    const [pageSize, setPageSize] = React.useState(25);
    const [sortField, setSortField] = React.useState("sentOn");
    const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">(
        "desc",
    );
    const [selectedEntry, setSelectedEntry] =
        React.useState<SendLogEntry | null>(null);
    const [preview, setPreview] =
        React.useState<SendLogEmailPreview | null>(null);
    const [isPreviewLoading, setIsPreviewLoading] = React.useState(false);
    const [previewError, setPreviewError] = React.useState<string>();
    const [isChatCreating, setIsChatCreating] = React.useState(false);

    const dataGridStore = React.useMemo(() => {
        const store = new AcxDataGridStore("KboSendLog", "Kbo");
        store.checkboxSelection = false;
        store.disableRowSelectionOnClick = true;
        store.disableLocalConfig = true;
        store.hideVertIcon = true;
        store.hideFilter = true;
        // Filters live in their own Paper above the grid, not AcxDataGrid's built-in header row.
        store.hideHeader = true;
        store.removeHeight = "0px";
        store.rowHeightOverride = true;
        store.pagination = true;
        store.paginationMode = "server";
        store.paginationModel = { page: 0, pageSize: 25 };
        store.pageSizeOptions = PAGE_SIZE_OPTIONS;
        store.onPaginationModelChange = (model) => {
            store.paginationModel = model;
            setPage(model.page);
            setPageSize(model.pageSize);
        };
        store.sortingMode = "server";
        store.sortModel = [{ field: "sentOn", sort: "desc" }];
        store.sortRequest = (model: GridSortModel) => {
            const sort = model[0];
            setSortField(sort?.field ?? "sentOn");
            setSortDirection((sort?.sort as "asc" | "desc") ?? "desc");
            store.paginationModel = { ...store.paginationModel, page: 0 };
            setPage(0);
        };
        store.onCellClick = (params) => {
            setSelectedEntry(params.row as SendLogEntry);
        };
        return store;
    }, []);

    React.useEffect(() => {
        const columns: IColDef[] = [
            {
                field: "title",
                headerName: "Research Question",
                flex: 2,
                minWidth: 260,
                // Resolved server-side from the run's stored output after the page query runs,
                // rather than a real column — there's nothing for the database to sort on.
                sortable: false,
                renderCell: (params) => {
                    return (
                        <Typography fontWeight={700}>{params.value}</Typography>
                    );
                },
            },
            {
                field: "recipients",
                headerName: "Recipients",
                flex: 1.2,
                minWidth: 220,
                renderType: "arrayJoinFormatter",
            },
            {
                field: "sentOn",
                headerName: "Sent",
                width: 160,
                ...dateTimeColumnType,
            },
            {
                field: "status",
                headerName: "Status",
                width: 180,
                sortable: false,
                renderCell: (
                    params: GridRenderCellParams<SendLogEntry>,
                ) => {
                    const presentation = getSendStatusPresentation(
                        params.row.status,
                    );
                    const isFailed =
                        params.row.status?.trim().toLowerCase() === "failed";
                    return (
                        <Stack spacing={0.25} paddingY={0.75}>
                            <AcxChip
                                size="small"
                                color={presentation.color}
                                label={presentation.label}
                                customMaterialIcon={statusIcon(
                                    params.row.status,
                                )}
                            />
                            {isFailed && params.row.failureSummary && (
                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    {params.row.failureSummary}
                                </Typography>
                            )}
                        </Stack>
                    );
                },
            },
        ];

        void dataGridStore.setColumns(columns, true);
    }, [dataGridStore]);

    React.useEffect(() => {
        const controller = new AbortController();
        dataGridStore.setLoading(true);

        service
            .getSendLog({
                sourceType: "KboFlowRun",
                recipient: debouncedRecipient || undefined,
                startDate: startDate?.toISOString(),
                endDate: endDate?.toISOString(),
                sortField,
                sortDirection,
                // The grid's pagination model is 0-based; the API is 1-based.
                page: page + 1,
                pageSize,
                abortSignal: controller.signal,
            })
            .then((result) => {
                dataGridStore.setRows(
                    result.items.map((item) => ({
                        id: item.sendCorrelationId,
                        ...item,
                    })),
                );
                dataGridStore.rowCount = result.totalCount;
            })
            .catch((error) => {
                if (controller.signal.aborted) return;
                messageStore.logError(
                    error instanceof Error
                        ? error.message
                        : "The send log could not be loaded.",
                );
            })
            .finally(() => {
                if (!controller.signal.aborted) dataGridStore.setLoading(false);
            });

        return () => controller.abort();
    }, [
        dataGridStore,
        service,
        messageStore,
        debouncedRecipient,
        startDate,
        endDate,
        sortField,
        sortDirection,
        page,
        pageSize,
    ]);

    React.useEffect(() => {
        if (!selectedEntry) {
            setPreview(null);
            setPreviewError(undefined);
            setIsPreviewLoading(false);
            return undefined;
        }

        const controller = new AbortController();
        setPreview(null);
        setPreviewError(undefined);
        setIsPreviewLoading(true);

        service
            .getSendLogPreview(
                selectedEntry.sendCorrelationId,
                "KboFlowRun",
                controller.signal,
            )
            .then(setPreview)
            .catch((error) => {
                if (controller.signal.aborted) return;
                setPreviewError(
                    error instanceof Error
                        ? error.message
                        : "The email preview could not be loaded.",
                );
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsPreviewLoading(false);
            });

        return () => controller.abort();
    }, [selectedEntry, service]);

    const reviewUrl = selectedEntry?.kboFlowRunId && adminOrganizationId
        ? `/admin/organizations/${adminOrganizationId}/kbo-flow-runs/${selectedEntry.kboFlowRunId}${
              selectedEntry.questionId
                  ? `?question=${encodeURIComponent(selectedEntry.questionId)}`
                  : ""
          }`
        : undefined;

    const createChatFromSend = async () => {
        if (!selectedEntry || isChatCreating) return;

        setIsChatCreating(true);
        try {
            const { chatId } = await service.createSendLogChat(
                selectedEntry.sendCorrelationId,
            );
            navigate(`/app/kbo/chats/${chatId}`);
        } catch (error) {
            messageStore.logError(
                error instanceof Error
                    ? error.message
                    : "The KBo chat could not be created.",
            );
        } finally {
            setIsChatCreating(false);
        }
    };

    return (
        <Stack
            rowGap={2}
            padding={3}
            height="100%"
            width="100%"
            sx={{
                overflow: "hidden",
                "& .MuiDataGrid-row": { cursor: "pointer" },
            }}
        >
            <Stack rowGap={0.5} flexShrink={0}>
                <Typography variant="h5" fontWeight={600}>
                    Send Log
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Every KBo Flow email sent to a client, with its delivery
                    outcome. Sort any column; filter by recipient or date range.
                </Typography>
            </Stack>
            <Paper
                sx={(theme) => ({ padding: theme.spacing(2), flexShrink: 0 })}
            >
                <Stack direction="row" columnGap={2}>
                    <AcxMainTextField
                        key="recipient-search"
                        id="send-log-recipient-search"
                        placeholderText="Search email address"
                        labelText="Recipient"
                        value={recipient}
                        onChange={(event) => setRecipient(event.target.value)}
                        containerStyle={{ flex: 1 }}
                        fullWidth
                    />
                    <AcxDateRangeInput
                        key="sent-between"
                        labelText="Sent between"
                        defaultStartDate={startDate}
                        defaultEndDate={endDate}
                        onSelect={(start, end) => {
                            setStartDate(start);
                            setEndDate(end);
                        }}
                    />
                </Stack>
            </Paper>
            <Stack flex={1} minHeight={0}>
                <AcxDataGrid dataGridStore={dataGridStore} />
            </Stack>
            <Dialog
                open={!!selectedEntry}
                onClose={() => setSelectedEntry(null)}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Email preview</DialogTitle>
                <DialogContent dividers>
                    {isPreviewLoading ? (
                        <Stack rowGap={1.5}>
                            <Skeleton variant="text" width="60%" height={28} />
                            <Skeleton variant="rounded" height={280} />
                        </Stack>
                    ) : previewError ? (
                        <Typography color="error">{previewError}</Typography>
                    ) : !preview ? (
                        <Typography color="text.secondary">
                            No email content is available for this send.
                        </Typography>
                    ) : (
                        <Stack rowGap={2}>
                            <Stack rowGap={0.5}>
                                <Typography fontWeight={700}>
                                    {preview.subject || "KBo report"}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    To: {selectedEntry?.recipients.join(", ")}
                                </Typography>
                            </Stack>
                            {preview.htmlBody ? (
                                <Box
                                    sx={(theme) => ({
                                        border: `1px solid ${theme.palette.gray[200]}`,
                                        borderRadius: 1,
                                        color: theme.palette.gray[700],
                                        fontSize: 14,
                                        maxHeight: "55vh",
                                        overflow: "auto",
                                        padding: theme.spacing(2),
                                    })}
                                    dangerouslySetInnerHTML={{
                                        __html: sanitizeHtml(preview.htmlBody),
                                    }}
                                />
                            ) : (
                                <Typography whiteSpace="pre-wrap">
                                    {preview.plaintextBody ||
                                        "No rendered email content is available for this send."}
                                </Typography>
                            )}
                        </Stack>
                    )}
                </DialogContent>
                <DialogActions>
                    {reviewUrl && (
                        <AcxButton
                            color="primary"
                            fullWidth={false}
                            onClick={() => navigate(reviewUrl)}
                        >
                            Review analysis
                        </AcxButton>
                    )}
                    {authStore.isAuthcxUser() && authStore.canUserView("View KBo") && (
                        <AcxButton
                            color="primary"
                            fullWidth={false}
                            disabled={isChatCreating}
                            onClick={() => void createChatFromSend()}
                        >
                            Ask KBo
                        </AcxButton>
                    )}
                    <AcxButton
                        color="white"
                        fullWidth={false}
                        onClick={() => setSelectedEntry(null)}
                    >
                        Close
                    </AcxButton>
                </DialogActions>
            </Dialog>
        </Stack>
    );
});

export default SendLogPage;
